import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import messages from './local/index';

const COUNTRY_LANG_MAP: Record<string, string> = {
  JP: 'ja',
  KR: 'ko',
  VN: 'vi',
  TH: 'th',
  US: 'en',
  GB: 'en',
  AU: 'en',
  CA: 'en',
  NZ: 'en',
  IE: 'en',
  ZA: 'en',
  IN: 'en',
  PH: 'en',
  TW: 'zh-TW',
  HK: 'zh-TW',
  MO: 'zh-TW',
  CN: 'zh-TW',
  SG: 'zh-TW',
  MY: 'zh-TW',
};

/** Fetch with manual timeout — compatible with all browsers */
function fetchWithTimeout(url: string, timeoutMs: number): Promise<Response> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), timeoutMs);
    fetch(url)
      .then((res) => { clearTimeout(timer); resolve(res); })
      .catch((err) => { clearTimeout(timer); reject(err); });
  });
}

/**
 * Detect language via multiple methods (priority order):
 * 1. User's persisted choice  (localStorage tupim_lang)
 * 2. ip-api.com  (free, no key, works on http + https)
 * 3. ipapi.co    (fallback geo API)
 * 4. navigator.language  (browser locale as last resort)
 * 5. Default: 'zh-TW'
 */
async function detectLanguage(): Promise<string> {
  const stored = localStorage.getItem('tupim_lang');
  if (stored) return stored;

  // --- ip-api.com ---
  try {
    const res = await fetchWithTimeout('https://ip-api.com/json/?fields=countryCode', 4000);
    if (res.ok) {
      const data = await res.json() as { countryCode?: string };
      const country = (data.countryCode ?? '').toUpperCase();
      console.log('[i18n] ip-api.com country:', country);
      if (country) return COUNTRY_LANG_MAP[country] ?? 'en';
    }
  } catch (e) {
    console.warn('[i18n] ip-api.com failed:', e);
  }

  // --- ipapi.co ---
  try {
    const res = await fetchWithTimeout('https://ipapi.co/json/', 4000);
    if (res.ok) {
      const data = await res.json() as { country_code?: string };
      const country = (data.country_code ?? '').toUpperCase();
      console.log('[i18n] ipapi.co country:', country);
      if (country) return COUNTRY_LANG_MAP[country] ?? 'en';
    }
  } catch (e) {
    console.warn('[i18n] ipapi.co failed:', e);
  }

  // --- browser locale ---
  try {
    const navLang = (navigator.language || '').toLowerCase();
    console.log('[i18n] navigator.language:', navLang);
    if (navLang.startsWith('ja')) return 'ja';
    if (navLang.startsWith('ko')) return 'ko';
    if (navLang.startsWith('vi')) return 'vi';
    if (navLang.startsWith('th')) return 'th';
    if (navLang.startsWith('zh-tw') || navLang.startsWith('zh-hk') || navLang.startsWith('zh-mo')) return 'zh-TW';
    if (navLang.startsWith('en')) return 'en';
  } catch {
    // ignore
  }

  return 'en';
}

const initialLang = localStorage.getItem('tupim_lang') || 'en';

i18n
  .use(initReactI18next)
  .init({
    lng: initialLang,
    fallbackLng: 'en',
    debug: false,
    resources: messages,
    interpolation: {
      escapeValue: false,
    },
  })
  .then(() => {
    // Only auto-detect if user hasn't manually picked a language
    if (!localStorage.getItem('tupim_lang')) {
      detectLanguage().then((lang) => {
        console.log('[i18n] switching to detected language:', lang);
        i18n.changeLanguage(lang);
      });
    }
  });

export default i18n;